import * as React from 'react';
import { Button } from '../../../../../designSystem/button/Button';
import { rawColors, colors } from '../../../../../designSystem/colors/color';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { H3 } from '../../../../../designSystem/typography/Heading';
import { toDateString } from '../../../../../utils/date';
import { RetsinfoHarvestRecord } from '../../../../../amorg-common';
import styled from 'styled-components';
import { CountAvatar } from '../../../../../designSystem/avatar/CountAvatar';
import { ToggletipTheOne } from '../../../../../designSystem/ToggletipTheOne';
import { retsinfoHelper } from './retsinfoHelper';


type ButtonCalendarProps = {
    records: RetsinfoHarvestRecord[],
    selectedDate: Date,
    onSelectDate: (date: Date) => void,
    showHidden: boolean,
    disabled: boolean;
};
export function ButtonCalendar ({ records, selectedDate, onSelectDate, showHidden, disabled }: ButtonCalendarProps): JSX.Element
{
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cellCount = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

    return (
        <Flex flexDirection='column' gap={8}>
            <Flex justifyContent='space-between' alignItems='center' gap={8}>
                <Button
                    variant='text'
                    icon='arrows_chevron_chevronBack'
                    ariaLabel='Forrige måned'
                    disabled={disabled}
                    onClick={() => onSelectDate(new Date(year, month - 1, 1))} />
                <H3>{selectedDate.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })}</H3>
                <Button
                    variant='text'
                    icon='arrows_chevron_chevronForward'
                    ariaLabel='Næste måned'
                    disabled={disabled}
                    onClick={() => onSelectDate(new Date(year, month + 1, 1))} />
            </Flex>
            <CalendarGrid>
                {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(day => <small key={day}>{day}</small>
                )}
                {Array.from({ length: cellCount }, (_, index) =>
                {
                    const record = records.find(record => record.recordYear === year &&
                        record.recordMonth === month + 1 &&
                        record.recordDay === index - firstWeekday + 1
                    );

                    const day = index - firstWeekday + 1;
                    if (day < 1 || day > daysInMonth)
                        return <div key={index} />;

                    const date = new Date(year, month, day);
                    const dateState = retsinfoHelper.getDateState(record, date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const count = record?.changes.filter(change => showHidden || change.flag !== 'hide').length;
                    const status = count === undefined ? 'Ikke hentet' : `${count} ændringer vises`;

                    return (
                        <CalendarDayButton
                            key={index}
                            type='button'
                            $selected={day === selectedDate.getDate()}
                            aria-pressed={day === selectedDate.getDate()}
                            aria-label={`${toDateString(date)}: ${status}`}
                            title={status}
                            disabled={disabled || date > today}
                            onClick={() => onSelectDate(date)}>

                                
                            <span>{day}</span>

                            {(dateState === 'has-changes'  || dateState === 'has-changes-outdated' || dateState === 'no-changes') &&
                                <CountAvatar count={count === undefined ? 0 : count} />
                            }

                            {dateState === 'no-record' && date <= today &&
                                <ToggletipTheOne icon='interface_actions_download' iconColor={colors.icon.warning}>Ingen data hentet for pågældende dag</ToggletipTheOne>
                            }

                            {dateState === 'no-record-outdated' && date <= today &&
                                <ToggletipTheOne icon='interface_actions_download' iconColor={colors.icon.disabled}>Data kan ikke længere tilgås for den pågældende dag</ToggletipTheOne>
                            }

                        </CalendarDayButton>
                    );
                })}
            </CalendarGrid>
            <small>-: Ikke hentet. Tal: Antal viste ændringer.</small>
        </Flex>
    );
}

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 4px;
    text-align: center;
`;

const CalendarDayButton = styled.button<{ $selected: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    height: 60px;
    padding: 8px 0;
    border: 1px solid ${props => props.$selected ? rawColors.blue.dark : rawColors.gray.light};
    border-radius: 4px;
    background: ${props => props.$selected ? rawColors.blue.dark : rawColors.white};
    color: ${props => props.$selected ? rawColors.white : rawColors.gray.dark};
    font: inherit;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${props => props.$selected ? rawColors.blue.darkExtreme : rawColors.blue.lightExtreme};
        border-color: ${rawColors.blue.dark};
    }

    &:focus-visible {
        outline: 2px solid ${rawColors.blue.dark};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;